import React, { MouseEventHandler } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory } from 'react-router-dom';
import { createSession } from 'flow/actions/chat-actions';
import { handleCtiClick } from 'flow/actions/cti-actions';
import {
    createPresenceNotification,
    deletePresenceNotification,
    removeVoiceMessage,
} from 'flow/actions/extension-actions';
import { generatePath } from 'flow/utils/router-utils';
import { routes } from 'flow/types/routes';
import Divider from 'flow/components/ui-components/Divider';
import Icon from 'flow/components/ui-components/Icon';
import MenuItem from 'flow/components/ui-components/MenuItem';
import Skeleton from 'flow/components/ui-components/Skeleton';
import useAppContext from 'flow/hooks/useAppContext';
import useFeatureFlags, { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import { selectChatSessionByChatUser } from 'flow/selectors/chat-selectors';
import { getSelectedCtiContactByNumber } from 'flow/selectors/cti-integration-selectors';

import {
    getExtensionKeyFromContactKey,
    getChatUserKeyForExtensionWithContactKey,
    canSetPresenceNotification,
    getExtension,
    selectOwnExtensionKey,
    selectOwnUserHasCapabilityVideo1on1,
} from 'flow/selectors/ExtensionSelectors';

import { getHasPresenceNotification } from 'flow/selectors/selectors';
import { selectCallRecordFromKey } from 'flow/selectors/call-selectors';
import { CallRecordKey } from 'flow/types/EntityKeys';

import { isExtensionKey, voiceMessageUrlPath } from 'flow/utils';
import { ContactType } from 'flow/utils/extension-utils';
import CallButtonMenuItem from './CallButtonMenuItem';
import CallWidgetMenuItems from './CallWidgetMenuItems';
import EmailFieldMenuItem from './EmailFieldMenuItem';
import VideoCallButtonMenuItem from './VideoCallButtonMenuItem';
import {
    getIdentityInfo,
    showBankidVerification,
    IdentityInfo,
} from 'flow/utils/call-record-utils';
import NestedMenuItem from 'flow/components/ui-components/NestedMenuItem';
import { maskString } from 'flow/utils/string-utils';
import CopyToClipboardButton from 'flow/components/buttons/CopyToClipboardButton';
import { useModal } from 'flow/contexts/ModalProvider';

interface Props {
    callRecordKey: CallRecordKey;
    onClickCreateContact: MouseEventHandler;
}

const CallHistoryContextMenu = ({
    callRecordKey,
    onClickCreateContact,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { openModal } = useModal();

    const callRecord = useAppSelector((state) =>
        selectCallRecordFromKey(state, callRecordKey),
    );

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const ownUserHasVideo1on1Capability = useAppSelector(
        selectOwnUserHasCapabilityVideo1on1,
    );
    const { hasFeature } = useFeatureFlags();

    const integrationContact = useAppSelector((state) =>
        getSelectedCtiContactByNumber(
            state,
            callRecord?.getIn(['number', 'number']),
        ),
    );

    const chatUserKey = useAppSelector((state) =>
        getChatUserKeyForExtensionWithContactKey(
            state,
            // @ts-expect-error
            callRecord?.getIn(['contact', 'key']),
        ),
    );

    const chatSessionKey = useAppSelector((state) =>
        selectChatSessionByChatUser(state, chatUserKey, ownExtensionKey),
    );

    const targetExtensionKey = useAppSelector((state) =>
        getExtensionKeyFromContactKey(
            state,
            // @ts-expect-error
            callRecord?.getIn(['contact', 'key']),
        ),
    );

    const presenceNotify = useAppSelector((state) =>
        getHasPresenceNotification(state, ownExtensionKey, targetExtensionKey),
    );

    const targetExtension = useAppSelector((state) =>
        isExtensionKey(targetExtensionKey)
            ? getExtension(state, targetExtensionKey)
            : undefined,
    );

    const isNotAvailable = useAppSelector((state) =>
        canSetPresenceNotification(state, targetExtensionKey),
    );

    if (!callRecord) {
        return (
            <div className="animate-fade text-gray-200">
                <Skeleton.Icon className="size-8" />
            </div>
        );
    }

    const showNotifyWhenAvailable = callRecord.get('contact')
        ? isNotAvailable &&
          callRecord.getIn(['contact', 'type']) === ContactType.bound.key
        : false;

    const navigateToContact: MouseEventHandler = () => {
        if (integrationContact) {
            dispatch(
                handleCtiClick(integrationContact.get('contactRoute'), null, {
                    number: integrationContact.get('number'),
                }),
            );
        } else {
            openModal('callRecordContactModal', { callRecordKey });
        }
    };

    const identityInfo: IdentityInfo | null = getIdentityInfo(callRecord);

    const IDENTITY_CONFIG: Record<string, { src: string; label: string }> = {
        bankid: {
            src: 'https://www.joinflow.com/appdirectory/bankid_icon.png',
            label: i18next.t('bankid.verification'),
        },
        'freja eid': {
            src: 'https://www.joinflow.com/appdirectory/freja_icon.png',
            label: i18next.t('freja.verification'),
        },
        mitid: {
            src: 'https://www.joinflow.com/appdirectory/mitId_48.svg',
            label: i18next.t('mitid.verification'),
        },
    };
    const identityConfig = IDENTITY_CONFIG[identityInfo?.authenticatorType];

    const navigateToChat: MouseEventHandler = (event) => {
        event.preventDefault();
        event.stopPropagation();
        return chatSessionKey
            ? history.push(
                  generatePath(routes['messages.id'], { id: chatSessionKey }),
              )
            : dispatch(createSession(chatUserKey));
    };

    const togglePresenceNotification: MouseEventHandler = (event) => {
        if (presenceNotify) {
            dispatch(
                deletePresenceNotification(ownExtensionKey, targetExtension),
            );
        } else {
            dispatch(
                createPresenceNotification(ownExtensionKey, targetExtension),
            );
        }
    };

    const canMakeVideoCall =
        targetExtension &&
        ownUserHasVideo1on1Capability &&
        hasFeature(FeatureFlags.VIDEO_CANVAS);

    return (
        <>
            <CallButtonMenuItem
                fixedNumber={callRecord.getIn(['contact', 'fixed', 'number'])}
                technicalNumber={callRecord.getIn([
                    'contact',
                    'technicalNumber',
                    'number',
                ])}
                mobileNumber={callRecord.getIn(['contact', 'mobile', 'number'])}
                extensionToCall={targetExtension}
                unknownContactNumber={callRecord.getIn(['number', 'number'])}
            />

            {canMakeVideoCall && (
                <VideoCallButtonMenuItem
                    extensionKey={targetExtension.get('key')}
                />
            )}

            {chatUserKey ? (
                <MenuItem onClick={navigateToChat}>
                    {i18next.t('Tooltip.Title.SendMessage')}
                </MenuItem>
            ) : null}

            {!(callRecord.get('contact') || integrationContact) ? (
                <MenuItem onClick={onClickCreateContact}>
                    {i18next.t('Colleagues.Create.External.Title')}
                </MenuItem>
            ) : (
                <MenuItem onClick={navigateToContact}>
                    {i18next.t('Chat.Details.GoToContactCard')}
                </MenuItem>
            )}

            {(callRecord.getIn(['contact', 'email']) ||
                showNotifyWhenAvailable) && <Divider className="my-2" />}

            <EmailFieldMenuItem
                emailField={callRecord.getIn(['contact', 'email'])}
            />

            {showNotifyWhenAvailable && (
                <MenuItem onClick={togglePresenceNotification}>
                    <div>
                        {i18next.t(
                            'General.ExtensionBusyAlet.NotifyWhenAvailable',
                        )}
                    </div>
                    {presenceNotify ? (
                        <div>
                            <Icon iconKey={'done'} />
                        </div>
                    ) : null}
                </MenuItem>
            )}

            {callRecord.has('voiceMessage') && (
                <>
                    <Divider className="my-2" />

                    <a
                        href={voiceMessageUrlPath(
                            ownExtensionKey,
                            callRecord.getIn(['voiceMessage', 'uniqueId']),
                        )}
                        download={true}
                    >
                        <MenuItem>
                            <div className="flex items-center gap-x-2">
                                <Icon iconKey="get_app" />
                                {i18next.t('call.voicemail.download')}
                            </div>
                        </MenuItem>
                    </a>

                    <MenuItem
                        onClick={() =>
                            dispatch(
                                removeVoiceMessage(
                                    ownExtensionKey,
                                    callRecord.getIn([
                                        'voiceMessage',
                                        'uniqueId',
                                    ]),
                                    callRecord.get('key'),
                                ),
                            )
                        }
                    >
                        <div className="flex items-center gap-x-2">
                            <Icon iconKey="delete" />
                            {i18next.t('call.voicemail.delete')}
                        </div>
                    </MenuItem>
                </>
            )}

            <CallWidgetMenuItems callRecord={callRecord} />

            {showBankidVerification(callRecord) && identityInfo && (
                <NestedMenuItem
                    title={
                        <div className="flex gap-2">
                            {identityConfig && (
                                <>
                                    <img
                                        className="max-h-6 max-w-6 pe-1"
                                        src={identityConfig.src}
                                        alt=""
                                    />
                                    {identityConfig.label}
                                </>
                            )}
                        </div>
                    }
                >
                    <div className="flex items-center gap-2 text-xs/5 whitespace-nowrap">
                        <Icon iconKey="gppGood" size="1.25rem" />
                        {i18next.t('identity.verified', {
                            authenticatorType: identityInfo.authenticatorType,
                            spacing: '\xA0\xA0\xA0\xA0',
                            orderRef: identityInfo.orderRef
                                ? maskString(identityInfo.orderRef)
                                : '',
                        })}
                        <CopyToClipboardButton
                            data={identityInfo.orderRef ?? ''}
                            className="hover:bg-main hover:darken-0 size-4 hover:text-white"
                        />
                    </div>
                </NestedMenuItem>
            )}
        </>
    );
};

export default React.memo(CallHistoryContextMenu);
